import { useState } from "react";
import type { QuickFactsData, RecentNote } from "../types";
import {
  Phone,
  Mail,
  StickyNote,
  ChevronDown,
  User,
  Award,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import { cn } from "../lib/utils";
import { formatCurrency } from "../../utils/formatting";
import { formatDate, formatShortDate } from "../../utils/dateFormatters";
import { ClassificationBadges } from "./ClassificationBadges";
import { VisitFrequencyIndicator } from "./VisitFrequencyIndicator";
import { ActionItemsSection } from "./ActionItemsSection";

// ---------------------------------------------------------------------------
// QuickFactsCard — main component
// ---------------------------------------------------------------------------

interface QuickFactsCardProps {
  data: QuickFactsData | null;
  loading?: boolean;
  /** Non-null when the fetch failed. */
  error?: string | null;
  className?: string;
  /** Compact mode for chat header usage */
  compact?: boolean;
}

export function QuickFactsCard({
  data,
  loading = false,
  error = null,
  className,
  compact = false,
}: QuickFactsCardProps) {
  if (loading) {
    return <QuickFactsSkeleton compact={compact} className={className} />;
  }

  if (error) {
    return (
      <div className={cn("rounded-xl border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30 p-6 text-center text-sm text-red-600 dark:text-red-400", className)}>
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className={cn("rounded-xl border border-border bg-surface-elevated p-6 text-center text-sm text-text-muted", className)}>
        Select an account to view quick facts.
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-surface-elevated",
        compact ? "p-3" : "p-4",
        className,
      )}
    >
      {/* Company name + location */}
      <CompanyHeader
        name={data.company_name}
        city={data.city}
        state={data.state}
        compact={compact}
      />

      {/* Classification badges */}
      <ClassificationBadges
        industryType={data.industry_type}
        marketSegment={data.market_segment}
        companyGroup={data.company_group}
        className={compact ? "mt-2" : "mt-3"}
      />

      {/* Visit frequency */}
      <VisitFrequencyIndicator
        visitFrequency={data.visit_frequency}
        className={compact ? "mt-2" : "mt-3"}
      />

      {/* Sales metrics — shown in both compact and full modes */}
      {compact && (
        <SalesMetricsTable
          metrics={data.metrics}
          lastOrderDate={data.last_order_date}
          portalSlug={data.has_sage_link ? data.customer_slug : undefined}
          className="mt-2"
        />
      )}

      {/* Primary contact — show in compact mode too */}
      {compact && data.primary_contact?.name && (
        <PrimaryContactSection
          contact={data.primary_contact}
          className="mt-2"
        />
      )}

      {compact && data.is_sphere_member && (
        <SphereBadge className="mt-2" />
      )}

      {/* Action items count badge in compact mode */}
      {compact && data.action_items && data.action_items.length > 0 && (
        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
          <CheckCircle2 className="h-3 w-3" />
          {data.action_items.length} action item{data.action_items.length !== 1 ? "s" : ""}
        </div>
      )}

      {compact && <div className="mt-1" />}
      {!compact && (
        <>
          {/* Primary contact — only if name is available */}
          {data.primary_contact?.name && (
            <PrimaryContactSection
              contact={data.primary_contact}
              className="mt-4"
            />
          )}

          {/* Sales metrics table — always show with fallback */}
          <SalesMetricsTable
            metrics={data.metrics}
            lastOrderDate={data.last_order_date}
            portalSlug={data.has_sage_link ? data.customer_slug : undefined}
            className="mt-4"
          />

          {/* Recent notes */}
          {data.recent_notes && data.recent_notes.length > 0 && (
            <RecentNotesSection
              notes={data.recent_notes}
              className="mt-4"
            />
          )}

          {/* Action items */}
          {data.action_items && data.action_items.length > 0 && (
            <ActionItemsSection
              items={data.action_items}
              className="mt-4"
            />
          )}

          {/* Sphere 1 status */}
          {data.is_sphere_member && (
            <SphereBadge className="mt-4" />
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Company Header
// ---------------------------------------------------------------------------

function CompanyHeader({
  name,
  city,
  state,
  compact,
}: {
  name: string;
  city: string | null;
  state: string | null;
  compact: boolean;
}) {
  const location = [city, state].filter(Boolean).join(", ");
  return (
    <div>
      <h3
        className={cn(
          "font-semibold text-text-primary leading-tight",
          compact ? "text-base" : "text-lg",
        )}
      >
        {name}
      </h3>
      {location && (
        <p className="text-sm text-text-secondary mt-0.5">{location}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primary Contact
// ---------------------------------------------------------------------------

function PrimaryContactSection({
  contact,
  className,
}: {
  contact: QuickFactsData["primary_contact"];
  className?: string;
}) {
  if (!contact) return null;

  return (
    <div className={cn("space-y-1", className)}>
      <h4 className="text-xs font-medium uppercase tracking-wider text-text-muted">
        Primary Contact
      </h4>
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
          <User className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary truncate">
            {contact.name}
            {contact.role && (
              <span className="ml-1 font-normal text-text-secondary">
                · {contact.role}
              </span>
            )}
          </p>
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {contact.phone && (
              <a
                href={`tel:${contact.phone}`}
                className="inline-flex items-center gap-1 text-sm text-link hover:text-link-hover hover:underline"
              >
                <Phone className="h-3.5 w-3.5" />
                {contact.phone}
              </a>
            )}
            {contact.email && (
              <a
                href={`mailto:${contact.email}`}
                className="inline-flex items-center gap-1 text-sm text-link hover:text-link-hover hover:underline"
              >
                <Mail className="h-3.5 w-3.5" />
                {contact.email}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sales Metrics Table (2024 – present)
// ---------------------------------------------------------------------------

function SalesMetricsTable({
  metrics,
  lastOrderDate,
  portalSlug,
  className,
}: {
  metrics: QuickFactsData["metrics"];
  lastOrderDate: string | null;
  /** When set, renders a "View in Portal" link using the customer slug. */
  portalSlug?: string;
  className?: string;
}) {
  const currentYear = new Date().getFullYear();
  const ly = currentYear - 1;
  const tya = currentYear - 2;

  // Check if there is any meaningful sales data
  const hasData =
    metrics.ytd_sales > 0 ||
    metrics.lytd_sales > 0 ||
    metrics.ly_total_sales > 0 ||
    metrics.two_years_ago_total > 0 ||
    metrics.two_years_ago_ytd > 0 ||
    metrics.orders_6mo > 0 ||
    metrics.orders_12mo > 0 ||
    metrics.orders_24mo > 0;

  if (!hasData) {
    return null;
  }

  const rows: { label: string; value: string }[] = [];

  // 2024 full-year sales (two years ago, complete year)
  rows.push({
    label: `${tya}`,
    value: metrics.two_years_ago_total > 0 ? formatCurrency(metrics.two_years_ago_total) : "$0",
  });

  // 2025 full-year sales (last year, complete year)
  rows.push({
    label: `${ly}`,
    value: metrics.ly_total_sales > 0 ? formatCurrency(metrics.ly_total_sales) : "$0",
  });

  // 2026 YTD sales (current year, year-to-date)
  rows.push({
    label: `${currentYear} YTD`,
    value: formatCurrency(metrics.ytd_sales),
  });

  // YTD change vs last year same period
  if (metrics.ytd_vs_lytd_pct != null) {
    const isPositive = metrics.ytd_vs_lytd_pct >= 0;
    rows.push({
      label: "YTD Change",
      value: `${isPositive ? "+" : ""}${metrics.ytd_vs_lytd_pct.toFixed(1)}%`,
    });
  }

  // Last order date
  if (lastOrderDate) {
    rows.push({
      label: "Last Order",
      value: formatDate(lastOrderDate),
    });
  }

  // Order counts — separate rows for clarity
  if (metrics.orders_6mo > 0 || metrics.orders_12mo > 0 || metrics.orders_24mo > 0) {
    rows.push({ label: "Orders (6 mo)", value: String(metrics.orders_6mo) });
    rows.push({ label: "Orders (12 mo)", value: String(metrics.orders_12mo) });
    rows.push({ label: "Orders (24 mo)", value: String(metrics.orders_24mo) });
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-medium uppercase tracking-wider text-text-muted">
          Sales Metrics
        </h4>
        {portalSlug && (
          <a
            href={`/portal/customers/${portalSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-medium text-brand-primary hover:underline"
          >
            View in Portal
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>
      <div className="rounded-lg bg-surface-muted p-2.5">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td className="py-0.5 pr-3 text-text-secondary">{row.label}</td>
                <td className="py-0.5 text-right font-medium text-text-primary">
                  {row.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent Notes
// ---------------------------------------------------------------------------

function RecentNotesSection({
  notes,
  className,
}: {
  notes: RecentNote[];
  className?: string;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <h4 className="text-xs font-medium uppercase tracking-wider text-text-muted">
        Recent Notes
      </h4>
      <div className="space-y-1.5">
        {notes.slice(0, 3).map((note) => (
          <NoteItem key={note.id} note={note} />
        ))}
      </div>
    </div>
  );
}

function NoteItem({ note }: { note: RecentNote }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = note.text.length > 120;
  const displayText = expanded || !isLong ? note.text : `${note.text.slice(0, 120)}...`;
  const dateStr = formatShortDate(note.created_at);

  return (
    <button
      type="button"
      onClick={() => isLong && setExpanded(!expanded)}
      className={cn(
        "w-full rounded-lg bg-surface-muted p-2.5 text-left",
        isLong && "cursor-pointer active:bg-surface-inset",
        !isLong && "cursor-default",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted" />
          <p className="text-xs text-text-secondary leading-relaxed">
            {displayText}
          </p>
        </div>
        {isLong && (
          <ChevronDown
            className={cn(
              "mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted transition-transform",
              expanded && "rotate-180",
            )}
          />
        )}
      </div>
      <div className="mt-1 flex items-center gap-2 text-[10px] text-text-muted">
        {note.contact_name && <span>{note.contact_name}</span>}
        <span>{dateStr}</span>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Sphere Badge
// ---------------------------------------------------------------------------

function SphereBadge({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1",
        "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
        "text-xs font-medium",
        className,
      )}
    >
      <Award className="h-3.5 w-3.5" />
      Sphere 1 Member
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading Skeleton
// ---------------------------------------------------------------------------

function QuickFactsSkeleton({
  compact,
  className,
}: {
  compact: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-surface-elevated animate-pulse",
        compact ? "p-3" : "p-4",
        className,
      )}
      aria-label="Loading quick facts"
    >
      {/* Company name */}
      <div className={cn("rounded bg-surface-inset", compact ? "h-5 w-48" : "h-6 w-56")} />
      {/* Location */}
      <div className="mt-1.5 h-4 w-24 rounded bg-surface-inset" />
      {/* Badges */}
      <div className="mt-3 flex gap-1.5">
        <div className="h-5 w-16 rounded-full bg-surface-inset" />
        <div className="h-5 w-20 rounded-full bg-surface-inset" />
        <div className="h-5 w-24 rounded-full bg-surface-inset" />
      </div>
      {/* Visit frequency */}
      <div className="mt-3 h-4 w-52 rounded bg-surface-inset" />

      {!compact && (
        <>
          {/* Contact */}
          <div className="mt-4 flex gap-3">
            <div className="h-8 w-8 rounded-full bg-surface-inset" />
            <div className="flex-1 space-y-1.5">
              <div className="h-4 w-40 rounded bg-surface-inset" />
              <div className="h-3 w-32 rounded bg-surface-inset" />
            </div>
          </div>
          {/* Sales */}
          <div className="mt-4 space-y-1.5">
            <div className="h-3 w-16 rounded bg-surface-inset" />
            <div className="flex items-center gap-2">
              <div className="h-6 w-24 rounded bg-surface-inset" />
              <div className="h-5 w-16 rounded-full bg-surface-inset" />
            </div>
          </div>
          {/* Notes */}
          <div className="mt-4 space-y-1.5">
            <div className="h-3 w-24 rounded bg-surface-inset" />
            <div className="h-12 w-full rounded-lg bg-surface-inset" />
            <div className="h-12 w-full rounded-lg bg-surface-inset" />
          </div>
        </>
      )}
    </div>
  );
}
